import { useState, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useLocation } from "wouter";

export default function CobrancaHistorico() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();

  const [busca, setBusca] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  const { data, isLoading, refetch } = trpc.cobrancaHistorico.list.useQuery(
    {
      busca: busca || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      limit: 100,
    },
    { enabled: !!user }
  );

  const rows = data?.rows || [];
  const total = data?.total || 0;

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Histórico de Cobranças</h1>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-1" />
          Atualizar
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar por pedido, destinatário ou canal..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="pl-9"
          />
        </div>
        <Input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="w-[150px]"
        />
        <Input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="w-[150px]"
        />
      </div>

      {/* Tabela */}
      <div className="border rounded-lg overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="p-3 text-left font-medium">Data/Hora</th>
              <th className="p-3 text-left font-medium">Pedido</th>
              <th className="p-3 text-left font-medium">Destinatário</th>
              <th className="p-3 text-left font-medium">Canal</th>
              <th className="p-3 text-left font-medium">Status</th>
              <th className="p-3 text-left font-medium">Enviado Por</th>
              <th className="p-3 text-left font-medium">Erro</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="p-4 text-center text-gray-500">
                  Carregando...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-4 text-center text-gray-500">
                  Nenhum registro encontrado.
                </td>
              </tr>
            ) : (
              rows.map((row: any) => (
                <tr key={row.id} className="border-b hover:bg-gray-50">
                  <td className="p-3">
                    {row.enviadoEm
                      ? format(new Date(row.enviadoEm), "dd/MM/yyyy HH:mm", { locale: ptBR })
                      : "-"}
                  </td>
                  <td className="p-3 font-medium">#{row.pedidoId}</td>
                  <td className="p-3">{row.destinatarioNome || "-"}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      row.canal === "email"
                        ? "bg-blue-100 text-blue-700"
                        : row.canal === "whatsapp"
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-700"
                    }`}>
                      {row.canal}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      row.status === "sucesso"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="p-3">{row.enviadoPorNome || "-"}</td>
                  <td className="p-3 text-red-600 text-xs max-w-[200px] truncate" title={row.erro || ""}>
                    {row.erro || "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="text-sm text-gray-500">
        Total: {total} registro(s)
      </div>
    </div>
  );
}
